import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { FromService } from 'src/app/services/form/from.service';
import { FormFieldItem, FormItem, Lang, FormPageItem } from 'src/app/models/forms';
import { FieldModalComponent } from '../field-modal/field-modal.component'; // ქვემოთ დაგიწერ
import { CommonModule } from '@angular/common';
import { SpinnerComponent } from "src/app/components/layout/spinner/spinner.component";
import { TextEditorComponent } from "src/app/components/layout/text-editor/text-editor.component";

@Component({
  selector: 'app-from-modal',
  imports: [CommonModule, DragDropModule, FormsModule, SpinnerComponent, ReactiveFormsModule, TextEditorComponent],
  templateUrl: './from-modal.component.html',
  styleUrl: './from-modal.component.css'
})
export class FromModalComponent implements OnInit {
  @Input() lang: Lang = 'geo';
  @Input() formId?: number;     // edit
  @Input() menuPage?: number;   // create for specific page

  loading = false;
  saving = false;

  form!: FormGroup;

  currentForm?: FormItem;
  pages: FormPageItem[] = [];
  fields: FormFieldItem[] = [];

  sorting = false;

  constructor(
    public activeModal: NgbActiveModal,
    private fb: FormBuilder,
    private api: FromService,
    private modal: NgbModal
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      title: ['', [Validators.required, Validators.maxLength(255)]],
      menu_page: [this.menuPage ?? null, [Validators.required]],
      recipient_emails: [''],
      subject: [''],
      success_message: ['მადლობა! შეტყობინება მიღებულია.'],
      layout: [1],
      content_html: [''],
      allow_files: [0],
      max_file_mb: [10],
    });

    this.load();
    this.loadPages();
  }

  load() {
    this.loading = true;

    if (!this.formId) {
      this.loading = false;
      return;
    }

    this.api.showForm(this.formId, this.lang).subscribe({
      next: (res) => {
        this.currentForm = res.data;
        this.form.patchValue({
          title: this.currentForm.title,
          menu_page: this.currentForm.menu_page,
          recipient_emails: this.currentForm.recipient_emails ?? '',
          subject: this.currentForm.subject ?? '',
          success_message: this.currentForm.success_message ?? '',
          layout: this.currentForm.layout ?? 1,
          content_html: this.currentForm.content_html ?? '',
          allow_files: this.currentForm.allow_files ?? 0,
          max_file_mb: this.currentForm.max_file_mb ?? 10,
        });

        this.loadFields();
      },
      error: () => (this.loading = false)
    });
  }

  loadFields() {
    if (!this.formId) {
      this.loading = false;
      return;
    }

    this.api.getFields(this.formId, this.lang).subscribe({
      next: (res) => {
        this.fields = (res.data ?? []).slice().sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));
        this.loading = false;
      },
      error: () => (this.loading = false)
    });
  }

  saveForm() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.saving = true;

    const payload = {
      ...this.form.value,
      lang: this.lang,
    };

    const req$ = this.formId
      ? this.api.updateForm(this.formId, payload, this.lang)
      : this.api.createForm(payload, this.lang);

    req$.subscribe({
      next: (res) => {
        this.currentForm = res.data;
        this.formId = res.data.id; // after create
        this.saving = false;
        this.loadFields();
      },
      error: () => (this.saving = false)
    });
  }

  openAddField() {
    if (!this.formId) return; // ჯერ ფორმა უნდა შეინახოს
    const ref = this.modal.open(FieldModalComponent, { size: 'lg', scrollable: true });
    ref.componentInstance.lang = this.lang;
    ref.componentInstance.formId = this.formId;

    ref.result.then(
      () => this.loadFields(),
      () => {}
    );
  }

  openEditField(field: FormFieldItem) {
    if (!this.formId) return;
    const ref = this.modal.open(FieldModalComponent, { size: 'lg', scrollable: true });
    ref.componentInstance.lang = this.lang;
    ref.componentInstance.formId = this.formId;
    ref.componentInstance.field = field;

    ref.result.then(
      () => this.loadFields(),
      () => {}
    );
  }

  deleteField(field: FormFieldItem) {
    if (!this.formId) return;
    if (!confirm('ველის წაშლა?')) return;

    this.api.deleteField(this.formId, field.id, this.lang).subscribe({
      next: () => this.loadFields()
    });
  }

  drop(ev: CdkDragDrop<FormFieldItem[]>) {
    if (!this.formId) return;

    moveItemInArray(this.fields, ev.previousIndex, ev.currentIndex);

    // send ids order (backend sets sort=1..N)
    const ids = this.fields.map(x => x.id);

    this.sorting = true;
    this.api.sortFields(this.formId, ids, this.lang).subscribe({
      next: () => (this.sorting = false),
      error: () => (this.sorting = false)
    });
  }

  loadPages() {
    this.api.getFormPages(this.lang).subscribe({
      next: (res) => {
        this.pages = (res.data ?? []) as FormPageItem[];

        // if menu_page was not preselected, pick the first available page
        const current = this.form.get('menu_page')?.value;
        if ((current === null || current === undefined || current === '') && this.pages.length) {
          this.form.patchValue({ menu_page: this.pages[0].menu_page });
        }
      },
      error: () => {
        this.pages = [];
      }
    });
  }
}